'use client';

import SportIcon from '@/app/activities/SportIcon';
import Loading from '@/app/components/Loading';
import { formatDistance } from '@trainme/api/client';
import type { Activity } from '@trainme/db/client';
import { format } from 'date-fns';

import { ActivityChart } from '@/app/activities/ActivityChart';
import { secondsToHm } from '@/utils/timeUtils';
import { ActivityWithRelations } from '@/utils/types';
import { useState } from 'react';
import { CalendarDayActivityModal } from './CalendarDayActivitiesModal';
import { useUnitSystem } from '@/app/hooks/useUnitSystem';

type Props = {
  activities: Activity[];
  isLoading?: boolean;
};

export function CalendarDayActivities({
  activities,
  isLoading = false,
}: Props) {
  const [showModal, setShowModal] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState<Activity | null>(
    null
  );

  const hasActivities = activities && activities.length > 0;

  // Only show loading if we're loading AND there's no data yet
  // This prevents showing loading spinner when scrolling through already-loaded data
  return (
    <div>
      {!hasActivities &&
        (isLoading ? (
          <div className='border border-emerald-400/30 rounded-2xl p-6 min-h-[120px] flex items-center justify-center'>
            <Loading
              size={16}
              className='text-emerald-400 dark:text-emerald-800'
            />
          </div>
        ) : (
          <div className='opacity-0 pointer-events-none' />
        ))}
      {hasActivities && (
        <ul className='shadow-sm'>
          {activities.map((activity, index) => (
            <li
              key={`activity-${activity.id}-${index}`}
              className='cursor-pointer border-l-4 rounded-lg border-emerald-400 my-1 overflow-hidden'
              onClick={() => {
                setSelectedActivity(activity);
                setShowModal(true);
              }}
            >
              <CalendarDayActivity activity={activity} />
            </li>
          ))}
        </ul>
      )}
      {showModal && selectedActivity && (
        <CalendarDayActivityModal
          activity={selectedActivity}
          isModalOpen={showModal}
          close={() => setShowModal(false)}
        />
      )}
    </div>
  );
}

export function CalendarDayActivity({
  activity,
}: {
  activity: ActivityWithRelations;
}) {
  const unitSystem = useUnitSystem();
  const strengthSets = activity.strengthSets ?? [];
  const isStrengthTraining =
    strengthSets.length > 0 || activity.sportType === 'WeightTraining';
  const activeStrengthSets = strengthSets.filter(
    (set) => set.setType !== 'rest' && !set.isRest
  );
  const strengthExerciseCount = new Set(
    activeStrengthSets.map(
      (set) => set.exerciseName ?? set.exerciseCategory ?? 'Exercise'
    )
  ).size;
  const strengthRepCount = activeStrengthSets.reduce(
    (sum, set) => sum + (set.repetitionValue ?? 0),
    0
  );
  const hasLaps = Boolean(activity.laps && activity.laps.length > 0);
  return (
    <div className='card flex h-full w-full flex-col overflow-hidden bg-green-200 bg-opacity-30'>
      <div className='card-header p-2'>
        <div className='flex items-center gap-1.5 flex-1 min-w-0'>
          <SportIcon type={activity.sportType || ''} withColor={true} />
          <div
            className='flex-1 truncate text-sm text-gray-600 dark:text-gray-300'
            title={activity.name || ''}
          >
            {activity.name}
          </div>
        </div>
        <div className='text-xs text-gray-500 dark:text-gray-400 shrink-0'>
          {activity.startDate
            ? format(new Date(activity.startDate), 'h:mm a')
            : 'Invalid date'}
        </div>
      </div>
      <div className='card-body flex-1 p-2'>
        {isStrengthTraining ? (
          activeStrengthSets.length > 0 ? (
            <div className='w-full h-16 flex flex-col justify-center gap-1 rounded-lg px-2'>
              <div className='text-base font-semibold text-gray-800 dark:text-gray-100 truncate'>
                {strengthExerciseCount}{' '}
                {strengthExerciseCount === 1 ? 'exercise' : 'exercises'}
              </div>
              <div className='text-sm text-gray-500 dark:text-gray-400'>
                <span className='font-semibold'>
                  {activeStrengthSets.length}
                </span>{' '}
                sets · <span className='font-semibold'>{strengthRepCount}</span>{' '}
                reps
              </div>
            </div>
          ) : (
            <div className='w-full h-full flex justify-center items-center rounded-lg p-2 text-gray-500'>
              No strength data
            </div>
          )
        ) : (
          <>
            {hasLaps && (
              <div className='w-full h-16'>
                <ActivityChart activity={activity} />
              </div>
            )}
            {!hasLaps && (
              <div className='w-full h-full flex justify-center items-center rounded-lg p-2 text-gray-500'>
                No laps
              </div>
            )}
          </>
        )}
      </div>
      <div className='card-footer flex w-full items-center justify-between gap-4 p-2 border-t border-gray-200 dark:border-gray-700'>
        <div className='text-sm font-semibold text-gray-800 dark:text-gray-100'>
          {secondsToHm(activity.movingTime ?? 0)}
        </div>
        <div className='text-sm font-semibold text-gray-800 dark:text-gray-100'>
          {activity.distance && activity.distance > 0
            ? formatDistance(activity.distance, unitSystem)
            : null}
        </div>
      </div>
    </div>
  );
}
